import { 
  collection, 
  getDocs, 
  addDoc,
  updateDoc,
  query, 
  where,
  doc,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

const ONGOING_SUBMISSIONS_COLLECTION = 'ongoingProjectSubmissions';

export const ongoingProjectSubmissionService = {
  
  /**
   * Get submission status for a department and financial year
   */
  async getSubmissionStatus(department, financialYear) {
    try {
      console.log('🔍 Getting submission status for:', { department, financialYear });
      
      const submissionsQuery = query(
        collection(db, ONGOING_SUBMISSIONS_COLLECTION),
        where('department', '==', department),
        where('financialYear', '==', financialYear)
      );
      
      const querySnapshot = await getDocs(submissionsQuery);
      
      if (querySnapshot.empty) {
        console.log('ℹ️ No submission found - returning null');
        return { 
          success: true, 
          data: null 
        };
      }
      
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      console.log('✅ Submission status found:', { 
        id: doc.id, 
        isLocked: data.isLocked,
        submittedAt: data.submittedAt?.toDate()
      });
      
      return {
        success: true,
        data: {
          id: doc.id,
          ...data,
          submittedAt: data.submittedAt?.toDate(),
          unlockedAt: data.unlockedAt?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        }
      };
    } catch (error) {
      console.error('❌ Error getting submission status:', error);
      throw new Error('Failed to load submission status: ' + error.message);
    }
  },

  /**
   * Submit/Lock ongoing projects for a department
   */
  async submitOngoingProjects(department, financialYear, userId, userName) {
    try {
      console.log('🔒 Submitting ongoing projects for:', { department, financialYear, userId });
      
      // Check if submission already exists
      const existingSubmission = await this.getSubmissionStatus(department, financialYear);
      
      const submissionData = {
        department,
        financialYear,
        isLocked: true,
        submittedAt: serverTimestamp(),
        submittedBy: userId,
        submittedByName: userName,
        unlockedAt: null,
        unlockedBy: null,
        unlockedByName: null,
        updatedAt: serverTimestamp()
      };
      
      if (existingSubmission.data) {
        // Update existing submission
        console.log('📝 Updating existing submission:', existingSubmission.data.id);
        
        const submissionRef = doc(db, ONGOING_SUBMISSIONS_COLLECTION, existingSubmission.data.id);
        await updateDoc(submissionRef, submissionData);
        
        console.log('✅ Submission updated successfully');
        
        return {
          success: true,
          message: 'Ongoing projects submitted and locked successfully',
          data: { id: existingSubmission.data.id, ...submissionData }
        };
      } else {
        // Create new submission
        console.log('➕ Creating new submission');
        
        submissionData.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, ONGOING_SUBMISSIONS_COLLECTION), submissionData);
        
        console.log('✅ Submission created successfully:', docRef.id);
        
        return {
          success: true,
          message: 'Ongoing projects submitted and locked successfully',
          data: { id: docRef.id, ...submissionData }
        };
      }
    } catch (error) {
      console.error('❌ Error submitting ongoing projects:', error);
      throw new Error(error.message || 'Failed to submit ongoing projects');
    }
  },

  /**
   * Unlock ongoing projects for a department (Admin only)
   */
  async unlockOngoingProjects(department, financialYear, adminId, adminName) {
    try {
      console.log('🔓 Unlocking ongoing projects for:', { department, financialYear, adminId });
      
      const existingSubmission = await this.getSubmissionStatus(department, financialYear);
      
      if (!existingSubmission.data) {
        console.log('❌ No submission found to unlock');
        throw new Error('No submission found to unlock');
      }
      
      if (!existingSubmission.data.isLocked) {
        console.log('⚠️ Submission is already unlocked');
        throw new Error('Submission is already unlocked');
      }
      
      const submissionRef = doc(db, ONGOING_SUBMISSIONS_COLLECTION, existingSubmission.data.id);
      await updateDoc(submissionRef, {
        isLocked: false,
        unlockedAt: serverTimestamp(),
        unlockedBy: adminId,
        unlockedByName: adminName,
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ Ongoing projects unlocked successfully');
      
      return {
        success: true,
        message: 'Ongoing projects unlocked successfully',
        data: existingSubmission.data
      };
    } catch (error) {
      console.error('❌ Error unlocking ongoing projects:', error);
      throw new Error(error.message || 'Failed to unlock ongoing projects');
    }
  },

  /**
   * Get all submissions (Admin view)
   */
  async getAllSubmissions(financialYear = null) {
    try {
      let submissionsQuery = collection(db, ONGOING_SUBMISSIONS_COLLECTION);
      
      if (financialYear) {
        submissionsQuery = query(
          submissionsQuery,
          where('financialYear', '==', financialYear)
        );
      }
      
      const querySnapshot = await getDocs(submissionsQuery);
      const submissions = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        submissions.push({
          id: doc.id,
          ...data,
          submittedAt: data.submittedAt?.toDate(),
          unlockedAt: data.unlockedAt?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        });
      });
      
      return {
        success: true,
        data: submissions
      };
    } catch (error) {
      console.error('Error fetching all submissions:', error);
      throw new Error('Failed to load submissions: ' + error.message);
    }
  },

  /**
   * NEW: Get list of departments with locked submissions for a financial year
   */
  async getLockedDepartments(financialYear) {
    try {
      const q = query(
        collection(db, ONGOING_SUBMISSIONS_COLLECTION),
        where('financialYear', '==', financialYear),
        where('isLocked', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      const departments = [];
      
      querySnapshot.forEach((doc) => {
        departments.push(doc.data().department);
      });
      
      return { success: true, data: departments };
    } catch (error) {
      console.error('Error getting locked departments:', error);
      throw new Error('Failed to load locked departments');
    }
  },

  /**
   * Check if department can edit (not locked)
   */
  async canDepartmentEdit(department, financialYear) {
    try {
      const submission = await this.getSubmissionStatus(department, financialYear);
      
      // Can edit if no submission exists or if submission is not locked
      const canEdit = !submission.data || !submission.data.isLocked;
      
      console.log('🔐 Can department edit?', { 
        department, 
        financialYear, 
        canEdit,
        isLocked: submission.data?.isLocked 
      });
      
      return canEdit;
    } catch (error) {
      console.error('Error checking edit permission:', error);
      return false;
    }
  }
};

export default ongoingProjectSubmissionService;